import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useGetTelehealthSessionInfo } from '../../hooks';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useVideoPlatform } from '../../hooks/useVideoPlatform';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { normalizeVideoPlatform } from '../../video-sdk';
import { visitorStateAtom, visitorStateToAppState } from '../state';

/**
 * Segment tracking for the system-notification permission events, mirroring
 * {@link useAdmissionSystemNotificationsTrackingEvents}. `componentName` is
 * derived from the visitor's current {@link AppState} (read at call time, since
 * these can fire from different points in the flow).
 */
export function useSystemNotificationsPermissionTrackingEvents() {
  const { userViewed, userClicked } = useTrackEvent();
  const { patientShortId, providerShortId } = useParams<{
    patientShortId: string;
    providerShortId: string;
  }>();
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId ?? '';
  const source = normalizeVideoPlatform(useVideoPlatform());
  const getVisitorState = useAtomCallbackMemoized((get) => get(visitorStateAtom), []);

  return useMemo(() => {
    const buildPayload = () => ({
      patientShortId: patientShortId ?? '',
      providerShortId: providerShortId ?? '',
      appointmentShortId,
      source: source ?? '',
      componentName: visitorStateToAppState(getVisitorState()) ?? '',
      componentType: 'page',
    });

    return {
      trackPermissionSoftPromptViewed: () =>
        userViewed(EventTypeEnum.SYSTEM_NOTIFICATIONS_PERMISSION_SOFT_PROMPT_VIEWED, buildPayload()),
      trackPermissionSoftPromptDismissed: () =>
        userClicked(EventTypeEnum.SYSTEM_NOTIFICATIONS_PERMISSION_SOFT_PROMPT_DISMISSED, buildPayload()),
      trackPermissionAllowed: () => userClicked(EventTypeEnum.SYSTEM_NOTIFICATIONS_PERMISSION_ALLOWED, buildPayload()),
      trackPermissionDenied: () => userClicked(EventTypeEnum.SYSTEM_NOTIFICATIONS_PERMISSION_DENIED, buildPayload()),
    };
  }, [patientShortId, providerShortId, appointmentShortId, source, getVisitorState, userViewed, userClicked]);
}
